/**
 * The one entity shape every membership document takes — and the reason this
 * package exists as code rather than as a convention someone remembers.
 *
 * `membershipEntity` splits an entity's policy in two, permanently:
 *
 *   - **reads are the product's** — `readPolicy` receives the full ICAN builder
 *     and returns whatever `read`/`list` rules that product's own model calls
 *     for. The two consumers' read gates are deliberate opposites (tickets:
 *     bare membership + second factor; momo-starter: manage-OR-ownRole with a
 *     bootstrap branch), and forcing them to converge would break one product's
 *     roster to serve the other's. So the slice never authors a read rule.
 *
 *   - **writes are the slice's** — every action named in `handlers` is emitted
 *     as `.requiresHandler(...)`, unconditionally, and there is no parameter
 *     through which a product can declare an FE-direct write on a membership
 *     document instead. That is the whole point. A declarative
 *     `hasPermission('roles.manage')` write gate cannot see the `roleId` being
 *     written or the permission set being minted, so a holder could author a
 *     role carrying every token in the catalog and then self-assign it through
 *     the grant door. The containment spine — caller holds the gating
 *     permission, target exists, target's power ⊆ caller's power, scope tenancy
 *     resolves — only fits in a callable. Routing every write to one is
 *     therefore not a default this factory picks; it is the invariant it exists
 *     to make unstateable otherwise.
 *
 * A product that wants a new write door on a membership entity has to name a
 * handler for it. It cannot open one by writing a rule.
 */
import type {
	Entity,
	EntityBuilder,
	EntitySeed,
	Rule,
	UserBase,
} from "@mhmo91/ican";
import { firestore } from "@mhmo91/ican/firestore/binding";
import type { z } from "zod";
import type { MembershipActor } from "./actor.ts";

/**
 * "This product adds no fields here." Spelled as an empty shape rather than
 * `Record<string, never>`, whose index signature widens every schema built from
 * it into `{[x: string]: never}` and erases the field types the callers infer.
 */
export type EmptyShape = Record<never, never>;

/**
 * The product-supplied half: read-side rules only. It receives the same builder
 * `.pipe(...)` would hand it, so an existing policy body ports across verbatim.
 */
export type ReadPolicy<
	User extends UserBase,
	Actions extends readonly string[],
	Schema extends z.ZodTypeAny,
> = (
	ctx: EntityBuilder<User, Actions, Schema>,
) => ReadonlyArray<Rule<User, z.infer<Schema>, Actions[number]>>;

/**
 * `entity<Actor>()` after its actor is pinned — what the product hands in.
 *
 * The seed is minted at the product, not here, because ICAN's own
 * index-signature guard resolves against a CONCRETE actor: it is what turns
 * `me.<typo>` into a compile error instead of a silently-permissive rule. Pin
 * the actor behind a type parameter and the guard degrades to an unresolved
 * conditional that no longer catches anything. The slice would rather take one
 * extra argument than quietly disarm the check every rule it emits relies on.
 */
export type EntityFactory<User extends UserBase> = <
	Schema extends z.ZodTypeAny,
	const Actions extends readonly string[],
>(seed: {
	readonly schema: Schema;
	readonly actions: Actions;
	readonly namespace?: string;
}) => EntitySeed<User, Actions, Schema>;

export function membershipEntity<
	User extends UserBase & MembershipActor,
	const Actions extends readonly string[],
	Schema extends z.ZodTypeAny,
>(
	makeEntity: EntityFactory<User>,
	opts: {
		readonly schema: Schema;
		readonly actions: Actions;
		readonly namespace: string;
		readonly path: string;
		readonly hierarchicalGrants?: boolean;
		/** action → callable name. Every entry emits `.requiresHandler`, never a rule. */
		readonly handlers: Readonly<Partial<Record<Actions[number], string>>>;
		readonly readPolicy: ReadPolicy<User, Actions, Schema>;
	},
): Entity<User, Actions, Schema> {
	return makeEntity({
		schema: opts.schema,
		actions: opts.actions,
		namespace: opts.namespace,
	})
		.pipe((ctx) => [
			...opts.readPolicy(ctx),
			...Object.entries(opts.handlers).map(([action, handler]) =>
				ctx
					.can(action as Actions[number])
					.when(() => ctx.and())
					.requiresHandler(handler as string),
			),
		])
		.adaptor(
			firestore({
				path: opts.path,
				...(opts.hierarchicalGrants === undefined
					? {}
					: { hierarchicalGrants: opts.hierarchicalGrants }),
			}),
		);
}
